import {
	createContext,
	useState,
	ReactNode,
	useContext,
	useRef,
	useEffect,
} from 'react';
import { mapServices } from '../utils/pdfUtils';
import { useAuth } from '../hooks/useAuth';
import { cloneDeep, noop } from 'lodash';
import { useQuickoHelper } from 'src/hooks/useQuickoHelper';

interface ServicesOptions {
	label: string;
	labelRiepilogo: string;
	value: string | number;
	isChecked: boolean;
	quantity?: number;
	unlimited?: boolean;
	data?: any;
}

export interface ServicesState {
	assicurazioneRC: ServicesOptions[];
	assicurazioneKasko: ServicesOptions[];
	furtoIncendio: ServicesOptions[];
	gomme: ServicesOptions[];
	tutelaLegale: ServicesOptions;
	// assicurazionePAI: ServicesOptions;
	manutenzione: ServicesOptions;
	vetturaSostitutiva: ServicesOptions[];
}

interface QuotReqState {
	checkedServices: string[];
	vehicleState: any;
	totalKm: number;
	rentalDuration: number;
	advancePayment: number;
	optionalTotals: number;
}

interface AiContextType {
	quotReqState: QuotReqState;
	setQuotReqState: React.Dispatch<React.SetStateAction<QuotReqState>>;
	activeStep: number;
	setActiveStep: React.Dispatch<React.SetStateAction<number>>;
	services: ServicesState;
	setServices: (services: any) => void;
	showResults: boolean;
	setShowResults: (value: boolean) => void;
	quotations: any;
	setQuotations: (quotation: any) => void;
	downloadPdf: (row: any) => any;
	error: string;
	setError: (row: any) => any;
	existDiscount: boolean;
	setExistDiscount: (value: boolean) => any;
	fullPriceVehicle: number;
	setFullPriceVehicle: (value: number) => any;
	cleanAiContext: () => void;
	processedRow: any;
	setProcessedRow: (value: any) => void;
	quickoHelper: ReturnType<typeof useQuickoHelper>;
	closeModals: () => void;
	setCloseModalsCallback: (callback?: Function | null) => void;
	showSignalingModal: boolean;
	openSignalingModal: (signalingData?: any) => void;
	closeSignalingModal: () => void;
	signalingData: any;
	fadWarning: string | null;
	setFadWarning: (warning: string | null) => void;
}

const makeLcvVetturaSostitutiva = (): ServicesOptions[] => ([
	{
		value: 'lcv_small',
		label: 'Small',
		labelRiepilogo: 'Vettura Sostitutiva: Small',
		isChecked: false,
	},
	{
		value: 'lcv_medium',
		label: 'Medium',
		labelRiepilogo: 'Vettura Sostitutiva: Medium',
		isChecked: false,
	},
	{
		value: 'lcv_large',
		label: 'Large',
		labelRiepilogo: 'Vettura Sostitutiva: Large',
		isChecked: false,
	},
]);

export const baseServices: ServicesState = {
	assicurazioneRC: [
		{
			value: 0,
			label: 'RC nessuna penale risarcitoria',
			labelRiepilogo: 'RC: nessuna penale risarcitoria',
			isChecked: true,
		},
		{
			value: 250,
			label: 'RC 250€ penale risarcitoria',
			labelRiepilogo: 'RC: penale risarcitoria 250€',
			isChecked: false,
		},
	],
	vetturaSostitutiva: [
		{
			value: 'citycar',
			label: 'City car',
			labelRiepilogo: 'Vettura Sostitutiva: City car',
			isChecked: false,
		},
		{
			value: 'media',
			label: 'Media',
			labelRiepilogo: 'Vettura Sostitutiva: Media',
			isChecked: false,
		},
		{
			value: 'superior',
			label: 'Superior',
			labelRiepilogo: 'Vettura Sostitutiva: Superior',
			isChecked: false,
		},
	],
	furtoIncendio: [
		{
			value: 0,
			label: '0%',
			labelRiepilogo: 'Furto e Incendio: 0%',
			isChecked: false,
		},
		{
			value: 10,
			label: '10% ',
			labelRiepilogo: 'Furto e Incendio: franchigia 10%',
			isChecked: true,
		},
	],
	assicurazioneKasko: [
		{
			value: 0,
			label: '0€',
			labelRiepilogo: 'Kasko: franchigia 0 €',
			isChecked: false,
		},
		{
			value: 500,
			label: '500€',
			labelRiepilogo: 'Kasko: franchigia 500 €',
			isChecked: true,
		},
		{
			value: 1000,
			label: '1000€',
			labelRiepilogo: 'Kasko: franchigia 1000 €',
			isChecked: false,
		},
	],
	// assicurazionePAI: {
	// 	value: 'si',
	// 	label: 'Assicurazione PAI',
	// 	labelRiepilogo: 'PAI: si',
	// 	isChecked: false,
	// },
	tutelaLegale: {
		value: 'si',
		label: 'Tutela Legale',
		labelRiepilogo: 'Tutela Legale: si',
		isChecked: true,
	},
	manutenzione: {
		value: 'si',
		label: 'Manutenzione Ordinaria e Straordinaria',
		labelRiepilogo: 'Manutenzione: si',
		isChecked: true,
	},
	gomme: [
		{
			value: 'quality',
			label: 'Quality/Performance',
			labelRiepilogo: 'Gomme: Quality/Performance',
			isChecked: false,
			quantity: 1,
			unlimited: false,
			data: { estive: 1, invernali: 1 },
		},
		{
			unlimited: false,
			value: 'premium',
			label: 'Premium',
			labelRiepilogo: 'Gomme: Premium',
			isChecked: false,
			quantity: 1,
			data: { estive: 1, invernali: 1 },
		},
	],
};

const defaultServices = cloneDeep(baseServices);

const defaultQuotReqState: QuotReqState = {
	checkedServices: [],
	vehicleState: {},
	totalKm: 0,
	rentalDuration: 0,
	advancePayment: 0,
	optionalTotals: 0,
};

const defaultContextValue: AiContextType = {
	quotReqState: defaultQuotReqState,
	setQuotReqState: () => { },
	activeStep: 2,
	setActiveStep: () => { },
	services: defaultServices,
	setServices: () => { },
	showResults: false,
	setShowResults: () => { },
	quotations: [],
	setQuotations: () => { },
	downloadPdf: () => { },
	error: '',
	setError: () => { },
	existDiscount: false,
	setExistDiscount: () => { },
	fullPriceVehicle: 0,
	setFullPriceVehicle: (value: number) => { },
	cleanAiContext: () => { },
	processedRow: {},
	setProcessedRow: (value: any) => { },
	quickoHelper: {
		isSegnalatore: false,
		canCreateReport: false,
		extractSignalingData: (data) => data,
		updateCommissions(bestOffer, inputCommissionValue) {
			return bestOffer;
		},
		computeSegnalatoreCommissions: (commissionValue: string) => {
			return commissionValue;
		},
		isZero: (value) => false,
		updateQuotationCommissions: (responseQuotations: any, commissionValue?: string) => responseQuotations,
	},
	closeModals: noop,
	setCloseModalsCallback: (callback) => false,
	showSignalingModal: false,
	openSignalingModal: noop,
	closeSignalingModal: noop,
	signalingData: null,
	fadWarning: null,
	setFadWarning: (warning: string | null) => { },
};

const AiContext = createContext<AiContextType>(defaultContextValue);

export const AiProvider = ({ children }: { children: ReactNode }) => {
	const [quotReqState, setQuotReqState] = useState(defaultQuotReqState);
	const [activeStep, setActiveStep] = useState(defaultContextValue.activeStep);
	const [services, setServices] = useState<any>(defaultServices);
	const [processedRow, setProcessedRow] = useState<any>({});
	const [showResults, setShowResults] = useState<boolean>(false);
	const [error, setError] = useState<string>('');
	const [quotations, setQuotations] = useState<[]>([]);
	const [existDiscount, setExistDiscount] = useState<boolean>(false);
	const [fullPriceVehicle, setFullPriceVehicle] = useState<number>(
		defaultContextValue.fullPriceVehicle
	);
	const { user } = useAuth();
	const quickoHelper = useQuickoHelper();
	const closeModalsRef = useRef(noop);
	const [showSignalingModal, setShowSignalingModal] = useState(false);
	const [signalingData, setSignalingData] = useState(null);
	const [fadWarning, setFadWarning] = useState<string | null>(null);

	const cleanAiContext = () => {
		setQuotReqState((prev) => {
			prev.checkedServices = [];
			return prev;
		});
		setServices(defaultServices);
		setQuotations([]);
		setShowResults(false);
		setActiveStep(2);
		setFadWarning(null)
	};

	const downloadPdf = async (row) => {
		const services = mapServices('', quotReqState.checkedServices);
		const pdfData = {
			lista_offerte: [
				{
					id: 'customPdf',
					durata: quotReqState.rentalDuration,
					km_totali: quotReqState.totalKm,
					// servizi: quotReqState.servizi,
					anticipo: quotReqState.advancePayment,
					// importo_azienda: quotations.canone,
					// importo_privato: quotReqState.importo_privato,
				},
			],
			pdf: {
				data_validita: 'test data_validita',
				name: 'test name',
				userName: user.attributes.given_name,
				userSurname: user.attributes.family_name,
				userMail: user.attributes.email,
				offerType: 'test offerType',
				offerId: 'customPdf',
				dealerDetail: { logo: '' },
				services,
			},
			dati_veicolo: quotReqState.vehicleState.dati_veicolo,
			optionals: quotReqState.vehicleState.optionals.filter(
				(el) => el.descrizione.length > 0
			),
			// image_list: vehicleState.vehicleState.image_list[0]
		};
		return pdfData;
		// setPdfData(pdfData)
	};

	const closeModals = () => {
		closeModalsRef.current?.();
	}

	const computeVetturaSostitutiva = async () => {
		try {
			const vehicleKind = quotReqState.vehicleState?.dati_veicolo?.vehicle_kind;
			if (!vehicleKind) return;

			const isLcv = ['vcom'].includes(vehicleKind?.toLowerCase())

			if (isLcv) {
				setServices((prev) => ({...prev, vetturaSostitutiva: makeLcvVetturaSostitutiva() }));
			}
		} catch (e) {
			console.warn('Failed to fetch jato vehicle', e)
		}
	}

	useEffect(() => {
		computeVetturaSostitutiva();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [quotReqState?.vehicleState?.dati_veicolo?.jato_id]);

	useEffect(() => {
		console.debug('[AiContext] Signaling data', signalingData)
	}, [signalingData])

	const aiContextData: AiContextType = {
		quotReqState,
		setQuotReqState,
		activeStep,
		setActiveStep,
		services,
		setServices,
		showResults,
		setShowResults,
		quotations,
		setQuotations,
		downloadPdf,
		error,
		setError,
		existDiscount,
		setExistDiscount,
		fullPriceVehicle,
		setFullPriceVehicle,
		cleanAiContext,
		processedRow,
		setProcessedRow,
		quickoHelper,
		closeModals,
		signalingData,
		setCloseModalsCallback: (callback) => {
			closeModalsRef.current = callback || noop;
		},
		showSignalingModal,
		openSignalingModal: (data: any) => {
			setSignalingData(data);
			setShowSignalingModal(true);
		},
		closeSignalingModal: () => {
			setShowSignalingModal(false);
			setSignalingData(null);
			console.debug('[AiContext] Cleaned signaling data.')
		},
		setFadWarning: (warning: string | null) => setFadWarning(warning),
		fadWarning,
	};

	return (
		<AiContext.Provider value={aiContextData}>{children}</AiContext.Provider>
	);
};

export const useAiContext = () => useContext(AiContext);

export default AiContext;
